import { Room, Cost } from "../../types/room";
import { IncomeValuationProcess } from "../../types/valuation-process";
import { v4 as uuidv4 } from 'uuid'
import { yieldPurchaseOnPerpetuityAnnually, yieldPurchaseOnPerpetuityQuarterly } from "./utils";

// Calculation
export const calculateRoomGrossRentalIncome = (room: Room) => {
    const percent = room.yearly_occupancy
        ? (room.yearly_occupancy/100)
        : 0
    const no = room.no ?? 0;
    const rate = room.rate ?? 0;
    return {
        ...room,
        gross_rental_income: no * rate * 365 * percent
    }
}

export const summaryCalculation = (data: {
    rooms: {id: number, name: string, no: number, rate: number, yearly_occupancy: number}[],
    food_revenue: number,
    other_revenue: number,
    opex: number,
    opex_choice_id: number,
    fb_cost: number,
    fb_choice_id: number,
    capital_rate: number,
    purchaser_cost: number,
    timing_id: number,
    costs: {id: number, name: string, cost: number, choice_id: number}[]
}) => {
    const calculatedRooms = data.rooms.map(calculateRoomGrossRentalIncome)
    const roomsRevenue = calculatedRooms.reduce((acc, room) => acc + room.gross_rental_income, 0)
    const foodRevenue = (data.food_revenue/100) * roomsRevenue
    const otherRevenue = (data.other_revenue/100) * roomsRevenue
    const totalRevenue = roomsRevenue + foodRevenue + otherRevenue

    const result = calculation({
        gri: {rooms: roomsRevenue, food: foodRevenue, other: otherRevenue, total: totalRevenue},
        opex: {percent: data.opex, choiceId: data.opex_choice_id},
        fb: {percent: data.fb_cost, choiceId: data.fb_choice_id},
        capitalRate: data.capital_rate,
        purchaserCost: data.purchaser_cost,
        timingId: data.timing_id,
        costs: data.costs
    })
    return result.value
}

export const calculation = (payload: {
    gri: {
        rooms: number | null,
        food: number | null,
        other: number | null,
        total: number | null
    },
    opex: {
        percent: number | null,
        choiceId: number | null
    },
    fb: {
        percent: number | null,
        choiceId: number | null
    },
    costs: Cost[],
    capitalRate: number | null,
    purchaserCost: number | null
    timingId: number | null
}) => {
    const {
        gri,
        opex,
        fb,
        timingId
    } = payload
    const grossRentalIncome = gri.total ?? 0;

    const opexFrom = getChoiceFrom(opex.choiceId, gri) ?? 0
    const opexRate = opex.percent ? opex.percent : 0
    const operatingExpenditures = (opexRate/100) * opexFrom; 

    const fbFrom = getChoiceFrom(fb.choiceId, gri) ?? 0
    const fbRate = fb.percent ? fb.percent : 0
    const fbCost = (fbRate/100) * fbFrom

    const otherAllowances = payload.costs.map(c => {
        const from = getChoiceFrom(c.choice_id, gri) ?? 0
        const rate = c.cost ? c.cost : 0
        const value = (rate/100) * from;

        return {
            name: c.name,
            rate,
            value,
            from,
            fromId: c.choice_id
        }
    })
    const totalOtherAllowances = otherAllowances.reduce((acc, x) => acc + x.value, 0)

    const allowances = operatingExpenditures + fbCost + totalOtherAllowances 
    const noi = grossRentalIncome - allowances

    const capitalRate = payload.capitalRate ?? 0
    const yieldPurchaseOnPerpetuityAtCapitalRate = timingId == 1
        ? yieldPurchaseOnPerpetuityAnnually(capitalRate)
        : timingId == 2
            ? yieldPurchaseOnPerpetuityQuarterly(capitalRate)
            : undefined
    const capitalisedRent = yieldPurchaseOnPerpetuityAtCapitalRate === undefined 
        ? undefined
        : yieldPurchaseOnPerpetuityAtCapitalRate * noi
    const purchaserCost = payload.purchaserCost ?? 0
    const totalPurchaserCost = capitalisedRent === undefined 
        ? undefined
        : (purchaserCost/100) * capitalisedRent
    const value = capitalisedRent == undefined
        ? undefined
        : totalPurchaserCost == undefined
            ? undefined
            : capitalisedRent - totalPurchaserCost

    return {
        grossRentalIncome,
        allowances,
        operatingExpenditures: {
            rate: opexRate,
            value: operatingExpenditures,
            from: opexFrom,
            fromId: opex.choiceId
        },
        fbCost: {
            rate: fbRate,
            value: fbCost,
            from: fbFrom,
            fromId: fb.choiceId
        },
        otherAllowances,
        noi,
        capitalRate,
        yieldPurchaseOnPerpetuityAtCapitalRate,
        capitalisedRent,
        purchaserCost,
        totalPurchaserCost,
        timingId,
        value
    }
}

const getChoiceFrom = (
    id: number|null, 
    gri: {
        rooms: number | null, 
        food: number | null,
        other: number | null,
        total: number | null
    }
): number | null => {
    if (!id) return null;
    if (id == 1) return gri.rooms;
    if (id == 2) return gri.food;
    if (id == 3) return gri.other;
    return gri.total;
}

// State
export const addRoom = (process: IncomeValuationProcess): IncomeValuationProcess => {
    return {
        ...process,
        data: {
            ...process.data,
            rooms: [...process.data.rooms, newRoom()]
        }
    }
}

export const removeRoom = (process: IncomeValuationProcess, id: number | string): IncomeValuationProcess => {
    return {
        ...process,
        data: {
            ...process.data,
            rooms: process.data.rooms.filter(room => room.id !== id)
        }
    }
}

export const addCost = (process: IncomeValuationProcess): IncomeValuationProcess => {
    return {
        ...process,
        data: {
            ...process.data,
            costs: [...process.data.costs, newCost()]
        }
    }
}

export const removeCost = (process: IncomeValuationProcess, id: number | string): IncomeValuationProcess => {
    return {
        ...process,
        data: {
            ...process.data,
            costs: process.data.costs.filter(room => room.id !== id)
        }
    }
}

export const updateState = (
    process: IncomeValuationProcess,
    payload: {
        costs: Cost[],
        rooms: Room[],
        foodBeverageRevenue: number | null,
        otherRevenue: number | null,
        capitalRate: number | null,
        purchaserCost: number | null,
        opex: number | null,
        fbCost: number | null,
        opexChoiceId: number | null,
        fbChoiceId: number | null,
        timingId: number | null
    }
): IncomeValuationProcess => {
    return {
        ...process,
        data: {
            ...process.data,
            costs: [...payload.costs],
            rooms: [...payload.rooms] ,
            foodBeverageRevenue: payload.foodBeverageRevenue,
            otherRevenue: payload.otherRevenue,
            capitalRate: payload.capitalRate,
            purchaserCost: payload.purchaserCost,
            opex: payload.opex,
            fbChoiceId: payload.fbChoiceId,
            fbCost: payload.fbCost,
            opexChoiceId: payload.opexChoiceId,
            timingId: payload.timingId
        }
    }
}

// Misc
export const newRoom =(): Room => {
    return {
        id: uuidv4(), 
        name: null,
        no: null,
        rate: null,
        yearly_occupancy: null
    }
}

export const mapRoom = (room_type: string, number: number): Room => {
    return {
        id: uuidv4(), 
        name: room_type,
        no: number,
        rate: null,
        yearly_occupancy: null
    }
}

export const newCost = (): Cost => {
    return {
        id: uuidv4(),
        name: null,
        cost: null,
        choice_id: null
    }
}